import { request } from "leatherman-js";
import Status from "@/classes/Status";

// id of elasticsearch addon id is hardcoded in config
const ES_ADDON_ID = process.env.VUE_APP_MAGENTO_ELASTICSEARCH_ADDON_ID;

let storeBuilderIds = [];
if (process.env.VUE_APP_STOREBUILDER_IDS) {
  storeBuilderIds = process.env.VUE_APP_STOREBUILDER_IDS.split(",");
}

let storeBuilderUiOverrideIds = [];
if (process.env.VUE_APP_STOREBUILDER_UI_OVERRIDE_IDS) {
  storeBuilderUiOverrideIds = process.env.VUE_APP_STOREBUILDER_UI_OVERRIDE_IDS.split(",");
}

let replacementPackages = {};
if (process.env.VUE_APP_REPLACEMENT_PACKAGES) {
  process.env.VUE_APP_REPLACEMENT_PACKAGES.split(",").forEach((pair) => {
    const [key, value] = pair.split(":");
    replacementPackages[key] = Number(value);
  });
}

let replacementLocations = [];
if (process.env.VUE_APP_REPLACEMENT_LOCATIONS) {
  replacementLocations = process.env.VUE_APP_REPLACEMENT_LOCATIONS.split(",");
}

const transformStoreBuilderBody = {
  industry: "industry",
  alreadySelling: "already_selling_products",
  currentPlatform: "platform_currently_using",
  howSell: "plan_to_sell",
  salesMobileOrder: "in_person_sales_needs[mobile_ordering]",
  salesMobileFunds: "in_person_sales_needs[mobile_point_of_sale]",
  salesMobileCatalog: "in_person_sales_needs[mobile_catalog]",
  catalogSize: "product_catalog_size",
  hasVariations: "has_variations[has_variations]",
  contentExplainer: "existing_content_types[explainer_video]",
  contentKeyDifferentiate: "existing_content_types[key_differentiating_features]",
  contentArticles: "existing_content_types[articles_on_other_sites_telling_your_story]",
  contentCaseStudies: "existing_content_types[case_studies_of_happy_customers]",
  contentReturnPolicy: "existing_content_types[amazing_return_policy]",
  contentSocial: "existing_content_types[social_proof]",
  contentOffers: "existing_content_types[special_offers]",
  contentCoupons: "existing_content_types[special_coupons]",
  contentNewsletter: "existing_content_types[email_newsletter_signups]",
  contentReviews: "existing_content_types[customer_reviews]",
  loadProducts: "demo_products[demo_products]",
};

const termMap = {
  monthly: 1,
  annually: 12,
};

const checkout = {
  namespaced: true,
  state: {
    appId: null,
    packageId: null,
    locationId: null,
    addonIds: [],
    nickname: null,
    couponCode: null,
    discount: 0,
    term: "monthly",
    hasBigCommerce: false,
    installApp: true,
    classification: null,
    affiliateId: null,
    email: null,
    tax: 0,
    taxStatus: new Status(),
    autoRedirect: false,
    storeBuilderIds,
    storeBuilderUiOverrideIds,
    storeBuilderFields: {},
    storeBuilderId: null,
    replacementPackages,
    replacementLocations,
    cartstack: true,
  },
  mutations: {
    SET_APP(state, payload) {
      state.appId = payload;
    },
    SET_PACKAGE(state, payload) {
      state.packageId = payload;
    },
    SET_TERM(state, payload) {
      state.term = payload || "monthly";
    },
    SET_BIGCOMMERCE(state, payload) {
      state.hasBigCommerce = payload;
    },
    SET_LOCATION(state, payload) {
      state.locationId = payload;
    },
    SET_NICKNAME(state, payload) {
      state.nickname = payload;
    },
    SET_SALES_TAX(state, payload) {
      state.tax = payload || 0;
    },
    SET_COUPON_CODE(state, payload) {
      state.couponCode = payload;
    },
    SET_DISCOUNT(state, payload) {
      state.discount = payload || 0;
    },
    SET_AUTO_INSTALL(state, payload) {
      state.installApp = typeof payload === "undefined" ? true : payload;
    },
    SET_AFFILIATE(state, payload) {
      state.affiliateId = Number(payload);
    },
    SET_CLASSIFICATION(state, payload) {
      state.classification = payload;
    },
    SET_EMAIL(state, payload) {
      state.email = payload;
    },
    SET_TAX_STATUS(state, payload) {
      state.taxStatus.value = payload;
    },
    SET_STOREBUILDER_ID(state, payload) {
      state.storeBuilderId = payload;
    },
    SET_STOREBUILDER_FIELDS(state, payload) {
      state.storeBuilderFields = { ...payload };
    },
    ADD_ADDON(state, addonId) {
      const found = !!state.addonIds.find((id) => id === Number(addonId));
      if (!found) {
        state.addonIds.push(Number(addonId));
      }
    },
    REMOVE_ADDON(state, addonId) {
      const index = state.addonIds.findIndex((id) => id === Number(addonId));
      if (index >= 0) {
        state.addonIds.splice(index, 1);
      }
    },
    SET_AUTO_REDIRECT(state, payload) {
      state.autoRedirect = payload;
    },
    SET_CARTSTACK(state, payload) {
      state.cartstack = payload;
    },
  },
  actions: {
    async createPlan({ state, dispatch, rootGetters }) {
      // get currently selected app
      const app = rootGetters["apps/getAppById"](state.appId);
      const includeTerm = app.type !== "generic";
      const location = rootGetters["locations/getLocationById"](state.locationId);

      let packageId = state.packageId;

      // certain packages are substituteded for others when certain locations are selected MAD-5754
      if (
        state.replacementLocations.includes(location.code) &&
        replacementPackages[state.packageId]
      ) {
        packageId = replacementPackages[state.packageId];
        console.log(
          `Replacing package id ${state.packageId} with ${packageId} (only for location ${location.code}) `
        );
      }

      const data = {
        appId: state.appId,
        packageId: packageId,
        installApp: state.installApp,
        locationId: state.locationId,
        nickname: state.nickname,
        affiliate: state.affiliateId,
        addonsIds: state.addonIds,
        hasCreds: state.hasBigCommerce, // TODO: this should be conditional for bigcommerce plans
        couponCode: state.couponCode,
        term: includeTerm ? termMap[state.term] : null,
        storeBuilderId: state.storeBuilderId,
      };

      const response = await request.makeParse("createPlan", {}, data);

      const affiliate = dispatch("affiliates/conversion", response, { root: true });
      console.log({ affiliate });

      return response;
    },
    reset({ commit }) {
      commit("SET_APP", null);
      commit("SET_PACKAGE", null);
      commit("SET_TERM", null);
      commit("SET_BIGCOMMERCE", null);
      commit("SET_LOCATION", null);
      commit("SET_NICKNAME", null);
      commit("SET_SALES_TAX", null);
      commit("SET_COUPON_CODE", null);
      commit("SET_DISCOUNT", null);
      commit("SET_AUTO_INSTALL", null);
      commit("SET_CLASSIFICATION", null);
      commit("SET_EMAIL", null);
      commit("SET_STOREBUILDER_ID", null);
      commit("SET_STOREBUILDER_FIELDS", {});
      commit("SET_TAX_STATUS", Status.UNINITIALIZED);
    },
    async calculateTax({ commit, state }, data = {}) {
      if (!state.appId || !state.packageId) {
        return 0;
      }

      commit("SET_TAX_STATUS", Status.LOADING);

      try {
        const salesTax = await request.makeParse(
          "getSalesTax",
          {},
          {
            appId: state.appId,
            packageId: state.packageId,
            term: state.term,
            addonIds: state.addonIds,
            discountCode: state.couponCode,
            planId: data.planId,
          }
        );

        commit("SET_TAX_STATUS", Status.LOADED);
        commit("SET_SALES_TAX", salesTax);

        return salesTax;
      } catch (e) {
        commit("SET_TAX_STATUS", Status.ERROR);
      }
    },
    checkFreeTrialLimit(context, { email, packageId }) {
      return request.makeParse("checkFreeTrialLimit", { email, packageId }, {});
    },
    setCouponCode({ commit }, payload) {
      commit("SET_COUPON_CODE", payload);
    },
    setStoreBuilderFields({ commit }, payload) {
      commit("SET_STOREBUILDER_FIELDS", payload);
    },
    setTax({ commit }, payload) {
      commit("SET_SALES_TAX", payload);
    },
    setDiscount({ commit }, payload) {
      commit("SET_DISCOUNT", payload);
    },
    setApp({ commit, dispatch, rootGetters, state }, appId) {
      // get currently selected app
      const app = rootGetters["apps/getAppById"](appId);

      // if app is not 'magento', make sure elasticsearch isn't included
      if (app?.type !== "magento") {
        // else just make sure elastic search isn't included anymore
        dispatch("removeAddon", ES_ADDON_ID);
      }

      commit("SET_APP", appId);

      if (state.cartstack) {
        // update cartstack cart, if one exists
        dispatch("cartstack/setApp", appId, { root: true });
      }
    },
    setPackage({ commit, dispatch, state }, packageId) {
      commit("SET_PACKAGE", packageId);
      if (state.cartstack) {
        dispatch("cartstack/setPackage", packageId, { root: true });
      }
    },
    setTerm({ commit, dispatch, state }, term) {
      commit("SET_TERM", term);
      if (state.cartstack) {
        dispatch("cartstack/setTerm", term, { root: true });
      }
    },
    setHasBigCommerce({ commit }, payload) {
      commit("SET_BIGCOMMERCE", payload);
    },
    setLocation({ commit, dispatch, state }, locationId) {
      commit("SET_LOCATION", locationId);
      if (state.cartstack) {
        dispatch("cartstack/setLocation", locationId, { root: true });
      }
    },
    setNickname({ commit }, payload) {
      commit("SET_NICKNAME", payload);
    },
    setInstallApp({ commit, dispatch, state, rootGetters }, enabled) {
      // get currently selected app
      const app = rootGetters["apps/getAppById"](state.appId);
      // get curently seleted location
      const location = rootGetters["locations/getLocationById"](state.locationId);

      // if app is magento, and install is set to 'true', elasticsearch must be included
      if (app?.type === "magento" && location?.type !== "aws" && enabled) {
        dispatch("addAddon", ES_ADDON_ID);
      } else {
        // else just make sure elastic search isn't included anymore
        dispatch("removeAddon", ES_ADDON_ID);
      }

      commit("SET_AUTO_INSTALL", enabled);
    },
    setAffiliate({ commit }, payload) {
      if (!payload) {
        return;
      }
      commit("SET_AFFILIATE", payload);
    },
    setClassification({ commit }, payload) {
      commit("SET_CLASSIFICATION", payload);
    },
    setEmail({ commit }, payload) {
      commit("SET_EMAIL", payload);
    },
    addAddon({ commit }, addonId) {
      if (!addonId) {
        return;
      }
      commit("ADD_ADDON", addonId);
    },
    removeAddon({ commit }, addonId) {
      if (!addonId) {
        return;
      }
      commit("REMOVE_ADDON", addonId);
    },
    autoRedirect({ commit }, enabled) {
      commit("SET_AUTO_REDIRECT", enabled);
    },
    cartstackActive({ commit }, active) {
      commit("SET_CARTSTACK", active);
    },
    setStoreBuilderId({ commit }, id) {
      commit("SET_STOREBUILDER_ID", id);
    },
    async getStoreBuilderId({ commit, state }) {
      const body = new FormData();

      // build url encoded form data body out of store builder fields object
      Object.keys(state.storeBuilderFields).forEach((key) => {
        let value = state.storeBuilderFields[key];
        const transformedKey = transformStoreBuilderBody[key];
        if (value) {
          if (typeof value === "boolean") {
            value = value ? "on" : "off";
          }
          body.append(transformedKey, value);
        }
      });

      const response = await request.direct({
        method: "POST",
        url: process.env.VUE_APP_STOREBUILDER_API,
        headers: {
          "X-Api-Key": process.env.VUE_APP_STOREBUILDER_API_KEY,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        data: body,
      });

      commit("SET_STOREBUILDER_ID", response.data.uuid);

      return response.data.uuid;
    },
  },
  getters: {
    getPackageFee: (state, getters) => (pack) => {
      if (!pack) {
        return 0;
      }
      const addonPrices = getters.sumAddons(pack);
      if (state.term === "monthly") {
        return pack.monthlyFee + pack.appPricing[state.appId] + addonPrices;
      } else {
        return pack.annualFee + addonPrices;
      }
    },
    getAddons: (state) => (pack) => {
      if (!pack) {
        return [];
      }
      return pack.addons.filter(({ id }) => state.addonIds.includes(id));
    },
    sumAddons: (state) => (pack) => {
      // get addons that have been select, and are allowed by the selected package
      const addons = pack.addons.filter(({ id }) => state.addonIds.includes(id));
      let sum = 0;
      // sum all the addon fees based on the selected term
      addons.forEach(({ monthlyFee, annualFee }) => {
        sum += state.term === "monthly" ? monthlyFee : annualFee;
      });

      return sum;
    },
    getTotal: (state, getters) => (pack) => {
      if (!pack) {
        return 0;
      }

      const packagePrice = getters.getPackageFee(pack);
      const tax = state.tax || 0;
      const discount = state.discount || 0;

      return packagePrice + tax - discount;
    },
    getPackageId: (state) => state.packageId,
    getAppId: (state) => state.appId,
    getNickname: (state) => state.nickname,
    getLocationId: (state) => state.locationId,
    getTerm: (state) => state.term,
    getTax: (state) => state.tax,
    getCouponCode: (state) => state.couponCode,
    getDiscount: (state) => state.discount,
    getClassification: (state) => state.classification,
    getEmail: (state) => state.email,
    getQueryParams: (state) => ({
      appId: state.appId,
      packageId: state.packageId,
      locationId: state.locationId,
      term: state.term,
      nickname: state.nickname,
      installApp: state.installApp === true ? 1 : 0,
      addonId: state.addonIds[0],
      couponCode: state.couponCode,
      hasBigCommerce: state.hasBigCommerce === true ? 1 : 0,
      affiliateId: state.affiliateId || null,
      storeBuilderId: state.storeBuilderId || null,
    }),
    getTaxStatus: (state) => state.taxStatus,
    getInstallApp: (state) => state.installApp,
    getAutoRedirect: (state) => state.autoRedirect,
    requireStoreBuilder: (state) =>
      state.packageId && state.storeBuilderIds.includes(`${state.packageId}`),
    requireStoreBuilderUi: (state) =>
      state.packageId && state.storeBuilderUiOverrideIds.includes(`${state.packageId}`),
  },
};

export default checkout;
